// A guardrail in the person's words (02-DESIGN/03-console/01-the-console.md §
// the words: a rule is *a guardrail*, its selector *what it guards*, its
// expression *the rule*, escalate *needs confirmation*, transform *hands the
// call to a transformer*; 03-the-guardrails-
// service.md § the console: what each guards, in words derived from the
// selector). The wire keeps its words; the page shows these.

import { subject } from "@impire-io/sdk";

import type { Outcome, Rule } from "../../wire/guard.ts";

/** guards says what a selector guards: the subjects it matches, in words. */
export function guards(selector: string): string {
  const toks = subject.tokens(selector);
  const [family, id, kind, ...rest] = toks;
  if (family !== subject.Family.workload || id === undefined) {
    return selector;
  }
  const where = id === "*" ? "every workload" : id === ">" ? "every workload" : `workload ${id}`;
  if (id === ">" || kind === undefined) {
    return `everything ${where} serves`;
  }
  if (kind === ">") {
    return `everything ${where} serves`;
  }
  const name = rest.join(".");
  if (kind === subject.TOKEN_TOOL) {
    if (name === "" || name === "*" || name === ">") {
      return `every tool of ${where}`;
    }
    return `the tool ${name} of ${where}`;
  }
  if (kind === subject.TOKEN_INFER) {
    if (name === "" || name === "*" || name === ">") {
      return `every request to ${where}`;
    }
    return `${name} requests to ${where}`;
  }
  if (kind === "*") {
    return name === "" ? `every verb of ${where}` : `${name} under every verb of ${where}`;
  }
  const verb = [kind, ...rest].join(".");
  return `${verb} on ${where}`;
}

/** canEscalate reports whether an expression's text names the escalate literal: what decides whether the confirmation block is shown. */
export function canEscalate(expression: string): boolean {
  return /(['"])escalate\1/.test(expression);
}

/** canTransform reports whether an expression's text names the transform literal: what decides whether the transformer block is shown. */
export function canTransform(expression: string): boolean {
  return /(['"])transform\1/.test(expression);
}

/** OUTCOME_WORDS are the four outcomes as a person reads them, wherever `Impire-Guard-Outcome` is shown. */
export const OUTCOME_WORDS: Readonly<Record<Outcome, string>> = {
  allow: "allowed",
  deny: "refused",
  escalate: "needs confirmation",
  transform: "transformed",
};

/** outcomeWords says an outcome header's value in words; an unknown value is shown as it came. */
export function outcomeWords(outcome: string): string {
  return (OUTCOME_WORDS as Record<string, string | undefined>)[outcome] ?? outcome;
}

/** The forms a rule field's refusal is shown beside: the service's field names (impire-sdk-go/guard/enforce: Validate). */
export type RuleField =
  | "id"
  | "selector"
  | "expression"
  | "escalation"
  | "escalation.approver"
  | "escalation.ttl"
  | "escalation.grant-ttl"
  | "escalation.context"
  | "transformer"
  | "transformer.name"
  | "transformer.timeout";

/** ruleField reads a refusal's field as one of the form's, or undefined for one the form does not draw. */
export function ruleField(field: string): RuleField | undefined {
  switch (field) {
    case "id":
    case "selector":
    case "expression":
    case "escalation":
    case "escalation.approver":
    case "escalation.ttl":
    case "escalation.grant-ttl":
    case "escalation.context":
    case "transformer":
    case "transformer.name":
    case "transformer.timeout":
      return field;
    default:
      return undefined;
  }
}

/** ruleDoes is the one fact under a guardrail's name: who may confirm, the transformer it may hand the call to, or that it allows or refuses. */
export function ruleDoes(r: Rule): string {
  const parts: string[] = [];
  if (r.escalation !== undefined) {
    parts.push(
      r.escalation.approver === "admin"
        ? "may need an admin's confirmation"
        : "may need the person's confirmation",
    );
  }
  if (r.transformer !== undefined) {
    parts.push(`may hand the call to ${r.transformer.name}`);
  }
  return parts.length === 0 ? "allows or refuses" : parts.join("; ");
}
